import { ViewportObserver } from "../viewport";
import { CullingObserver, CullingObserverEntry } from "../culling";
import * as AST from "~/parse/sfml-ast";

export class TestElement extends HTMLElement { }

export class SFMLElement extends HTMLElement {
  private slots: HTMLElement[] = [];

  public handleResize() { }

  protected addSlot(element: HTMLElement) {
    this.slots.push(element);
  }

  protected appendChildToShadow<T extends Node>(newChild: T): T {
    return super.appendChild(newChild);
  }

  public appendChild<T extends Node>(newChild: T): T {
    return this.appendChildToSlot(newChild);
  }

  private appendChildToSlot<T extends Node>(newChild: T): T {
    if (this.slots.length === 0) {
      super.appendChild(newChild);
      return newChild;
    }

    const slotName = newChild instanceof HTMLElement ? newChild.getAttribute("slot") : null;

    for (let slot of this.slots) {
      const targetSlotName = slot.getAttribute("name");
      if ((!slotName && !targetSlotName) || targetSlotName === slotName) {
        slot.appendChild(newChild);
        break;
      }
    }

    return newChild;
  }
}

export class SFMLCullingElement extends SFMLElement {
  private _visible = false;

  public get visible(): boolean {
    return this._visible;
  }

  static cullingObserver = new CullingObserver((entries: CullingObserverEntry[]) => {
    for (let entry of entries) {
      const target = entry.target;
      if (target instanceof SFMLCullingElement) {
        if (entry.intersectionRatio > 0) {
          if (!target.visible) target.handleVisible();
        } else {
          if (target.visible) target.handleHidden();
        }
      }
    }
  });

  protected connectedCallback() {
    SFMLCullingElement.cullingObserver.observe(this);
  }

  protected disconnectedCallback() {
    SFMLCullingElement.cullingObserver.unobserve(this);
  }

  public handleVisible() {
    this._visible = true;
  }

  protected handleHidden() {
    this._visible = false;
  }
}

export type AspectResizerCallback = () => void;

export class AspectResizer {
  static shared = new AspectResizer();

  private resizeTargets = <{ target: HTMLElement; dimension: "width" | "height"; orientation: string }[]>[];
  private notificationTargets = <AspectResizerCallback[]>[];

  public register(target: HTMLElement, dimension: "width" | "height", orientation: string) {
    this.resizeTargets.push({ target, dimension, orientation });
  }

  public listen(callback: AspectResizerCallback) {
    this.notificationTargets.push(callback);
  }

  public unlisten(cancelCallback: AspectResizerCallback) {
    for (let i = 0, n = this.notificationTargets.length; i < n; ++i) {
      const callback = this.notificationTargets[i];
      if (cancelCallback === callback) {
        this.notificationTargets.splice(i, 1);
      }
    }
  }

  private notify() {
    for (let i = 0, n = this.notificationTargets.length; i < n; ++i) {
      const callback = this.notificationTargets[i];
      callback();
    }

    ViewportObserver.shared.notify();
  }

  private resizeElement(target: HTMLElement, dimension: "width" | "height", orientation: string) {
    const aspectRatio = !orientation || orientation === "vertical" || orientation === "image-top" ? Number(target.getAttribute("aspect-ratio")) : 1;

    if (dimension === "height") {
      target.style.height = target.offsetWidth * aspectRatio + "px";
    } else {
      target.style.width = target.offsetHeight / aspectRatio + "px";
    }

    if (target instanceof SFMLElement) {
      target.handleResize();
    }
  }

  public resize(parentElement?: HTMLElement) {
    for (let { target, dimension, orientation } of this.resizeTargets) {
      if (parentElement && !parentElement.contains(target)) {
        continue;
      }

      this.resizeElement(target, dimension, orientation);
    }

    this.notify();
    ViewportObserver.shared.resize();
  }
}

export interface SFMLAnnotatable {
  annotate: (imageUrl: string) => void;
  isAnnotated: () => boolean;
  clearAnnotation: () => void;
}

export function isSFMLAnnotatable(object: any): object is SFMLAnnotatable {
  if (object instanceof SFMLElement) return object.getAttribute("annotatable") !== null;
  else return false;
}

export interface SFMLImpressionable {
  clientRects: () => { identifier: AST.ItemIdentifier; rect: ClientRect }[];
}

export function isSFMLImpressionable(object: any): object is SFMLImpressionable {
  if (object instanceof SFMLElement) return object.getAttribute("impressionable") !== null;
  else return false;
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/element/index.ts